/**
 * HOLD more options - drives the colour and size request dialog.
 *
 * The markup is a native <dialog> holding a three-step form. This script:
 *
 *   1. opens the dialog from any [data-hold-more-options-open] trigger,
 *   2. advances a step as soon as a choice is made, so choosing a colour is
 *      one tap rather than a tap and a "Next",
 *   3. carries the two choices into the last step as a sentence, so the email
 *      question is answered in the light of what it is for,
 *   4. posts the answer to the Google Sheet endpoint and shows the result in
 *      place, without ever navigating away from the product page.
 *
 * WHY NOT SHOPIFY'S CONTACT FORM
 * Because it will not accept a submission without an email address. The whole
 * point of asking colour and size first is that those two answers are worth
 * having on their own, from someone who will never hand over an inbox. The
 * sheet takes a row with an empty email column; Shopify would take nothing.
 * See snippets/hold-more-options.liquid for the rest of that reasoning.
 *
 * THE REQUEST
 * Apps Script web apps answer a cross-origin POST only if the browser does not
 * preflight it, so the body goes as text/plain - a "simple request" - with the
 * JSON inside. Setting application/json here would trigger an OPTIONS the
 * script cannot answer, and every write would fail.
 *
 * Progressive enhancement: with scripting off the trigger is hidden by the
 * <noscript> rule in snippets/hold-more-options-trigger.liquid, so nothing
 * here is load-bearing for a page that never runs it.
 */
(() => {
  const LAST_STEP = 3;

  /** Show one step, hide the rest, and keep the announced count in sync. */
  const showStep = (dialog, step) => {
    dialog.querySelectorAll('[data-hold-more-options-step]').forEach((node) => {
      node.hidden = Number(node.dataset.holdMoreOptionsStep) !== step;
    });

    const done = dialog.querySelector('[data-hold-more-options-done]');
    const count = dialog.querySelector('[data-hold-more-options-count]');
    if (done) done.hidden = true;
    if (count) count.hidden = false;

    const current = dialog.querySelector('[data-hold-more-options-current]');
    if (current) current.textContent = String(step);

    dialog.dataset.holdMoreOptionsAt = String(step);

    // Focus the step's own legend so the next question is read out, rather
    // than leaving focus on a control that is now hidden.
    const legend = dialog.querySelector(
      `[data-hold-more-options-step="${step}"] .hold-more-options__legend`
    );
    if (legend) {
      legend.setAttribute('tabindex', '-1');
      legend.focus({ preventScroll: true });
    }
  };

  /** Swap the whole form for the confirmation. */
  /**
   * `promised` is whether we may write to them: an address was given and the
   * consent box was ticked. Both halves matter - an address typed without
   * ticking is not permission, and the Skip path has neither.
   */
  const showDone = (dialog, promised) => {
    dialog.querySelectorAll('[data-hold-more-options-step]').forEach((node) => {
      node.hidden = true;
    });

    const count = dialog.querySelector('[data-hold-more-options-count]');
    if (count) count.hidden = true;

    const done = dialog.querySelector('[data-hold-more-options-done]');
    if (!done) return;
    done.hidden = false;

    const promise = done.querySelector('[data-hold-more-options-promise]');
    if (promise) promise.hidden = !promised;

    const status = done.querySelector('.form-status');
    if (status) status.focus({ preventScroll: true });
  };

  /** The human-readable label of the checked radio in a named group. */
  const chosen = (dialog, name) => {
    const input = dialog.querySelector(`input[name="${name}"]:checked`);
    if (!input) return null;
    const label = dialog.querySelector(`label[for="${input.id}"]`);
    if (!label) return input.value;
    // The label can carry a footnote ("on sale now"); the choice is the rest.
    const note = label.querySelector('.hold-more-options__note');
    const text = note ? label.textContent.replace(note.textContent, '') : label.textContent;
    return text.trim();
  };

  /** Restate both choices above the email field. */
  const writeSummary = (dialog) => {
    const target = dialog.querySelector('[data-hold-more-options-summary]');
    if (!target) return;

    const colour = chosen(dialog, 'colour');
    const size = chosen(dialog, 'size');
    if (!colour && !size) {
      target.textContent = '';
      return;
    }

    const parts = [];
    if (colour) parts.push(colour);
    if (size) parts.push(size.toLowerCase());
    target.textContent = `You picked: ${parts.join(', ')}.`;
  };

  const setError = (dialog, message) => {
    const box = dialog.querySelector('[data-hold-more-options-error]');
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  };

  const openDialog = (dialog, step) => {
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }
    if (step) showStep(dialog, step);
  };

  const closeDialog = (dialog) => {
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  };

  /**
   * Post one row to the sheet.
   *
   * `withEmail` false is the "Skip the email" path: the choices still go, the
   * email and consent columns stay empty. Returns true when the script
   * confirmed the write.
   */
  const send = async (dialog, form, withEmail) => {
    const endpoint = dialog.dataset.holdMoreOptionsEndpoint;
    if (!endpoint) return false;

    const data = new FormData(form);
    const payload = {
      token: dialog.dataset.holdMoreOptionsToken || '',
      colour: data.get('colour') || '',
      size: data.get('size') || '',
      email: withEmail ? (data.get('email') || '').trim() : '',
      consent: withEmail && data.get('consent') ? data.get('consent') : '',
      website: data.get('website') || '',
      page: window.location.pathname,
    };

    const response = await fetch(endpoint, {
      method: 'POST',
      // text/plain keeps this a simple request - see the note at the top.
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload),
      redirect: 'follow',
    });

    const result = await response.json();
    return result && result.ok === true;
  };

  /**
   * Tell Shopify's customer-events bus that a request was submitted, so a web
   * pixel can forward it to an ad platform. While the glove is out of stock
   * this is the only conversion a visitor can complete, so without it an ad
   * platform has no signal at all to optimise against.
   *
   * Deliberately carries no email and no consent value. The pixel only needs
   * to know that a submission happened and what was asked for; sending the
   * address would turn this into sharing a personal identifier with a third
   * party, which is not what the privacy policy describes.
   */
  const publishSubmission = (form, withEmail) => {
    try {
      const data = new FormData(form);
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish('more_options_submitted', {
          colour: data.get('colour') || '',
          size: data.get('size') || '',
          email_given: withEmail === true,
        });
      }
    } catch (error) {
      // Analytics must never break the form.
    }
  };

  const submit = async (dialog, withEmail) => {
    const form = dialog.querySelector('form');
    if (!form || form.dataset.sending === 'true') return;

    setError(dialog, '');

    const emailField = form.querySelector('input[type="email"]');
    const email = emailField ? emailField.value.trim() : '';

    if (withEmail) {
      // The email is optional, but an email that is offered must be usable,
      // and an address with no consent beside it is not one we may write to.
      if (!email) {
        setError(dialog, 'Add an email address, or choose "Skip the email".');
        emailField?.focus();
        return;
      }
      if (!emailField.checkValidity()) {
        setError(dialog, 'That email address does not look right.');
        emailField.focus();
        return;
      }
      const consent = form.querySelector('input[name="consent"]');
      if (consent && !consent.checked) {
        setError(dialog, 'Tick the box so we may email you.');
        consent.focus();
        return;
      }
    }

    form.dataset.sending = 'true';
    const button = form.querySelector('.hold-more-options__submit');
    const buttonText = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Sending...';
    }

    try {
      const ok = await send(dialog, form, withEmail);
      if (ok) {
        publishSubmission(form, withEmail);
        const data = new FormData(form);
        const promised = withEmail
          && (data.get('email') || '').trim() !== ''
          && Boolean(data.get('consent'));
        showDone(dialog, promised);
      } else {
        setError(dialog, 'That did not save. Please try again in a moment.');
      }
    } catch (error) {
      setError(dialog, 'That did not save - check your connection and try again.');
    } finally {
      form.dataset.sending = 'false';
      if (button) {
        button.disabled = false;
        button.textContent = buttonText;
      }
    }
  };

  /**
   * A choice is the "Next": picking a colour moves to the size, picking a size
   * moves to the email. The last step has no radios, so this cannot run past
   * it.
   *
   * Driven by both `change` and `click` because neither is enough alone. A
   * radio that is already checked - which is exactly what someone finds after
   * pressing Back - fires no `change` at all, so on `change` alone the dialog
   * would sit there refusing to move unless they picked a different answer.
   * `click` alone would miss the keyboard, where arrow keys change the
   * selection without a click.
   *
   * Both events arrive for one new choice, so the guard below makes the second
   * a no-op: by then the dialog has already left the step the radio is on.
   */
  const advanceFrom = (dialog, radio) => {
    const step = Number(
      radio.closest('[data-hold-more-options-step]')?.dataset.holdMoreOptionsStep || 0
    );
    if (!step || step >= LAST_STEP) return;
    if (Number(dialog.dataset.holdMoreOptionsAt || 1) !== step) return;

    writeSummary(dialog);
    showStep(dialog, step + 1);
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-hold-more-options-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.holdMoreOptionsOpen);
      if (dialog) {
        event.preventDefault();
        // Always start at the first question: a reopened dialog that resumes
        // halfway hides the choices already made.
        setError(dialog, '');
        openDialog(dialog, 1);
      }
      return;
    }

    const dialog = event.target.closest('dialog.hold-more-options');
    if (!dialog) return;

    if (event.target.closest('[data-hold-more-options-close]')) {
      event.preventDefault();
      closeDialog(dialog);
      return;
    }

    if (event.target.closest('[data-hold-more-options-back]')) {
      event.preventDefault();
      setError(dialog, '');
      showStep(dialog, Math.max(1, Number(dialog.dataset.holdMoreOptionsAt || 1) - 1));
      return;
    }

    if (event.target.closest('[data-hold-more-options-skip]')) {
      event.preventDefault();
      submit(dialog, false);
      return;
    }

    // Re-picking the answer already showing, typically after Back.
    const label = event.target.closest('.hold-more-options__choice');
    const radio = label && dialog.querySelector(`#${CSS.escape(label.htmlFor)}`);
    if (radio && radio.checked) advanceFrom(dialog, radio);
  });

  document.addEventListener('change', (event) => {
    const dialog = event.target.closest('dialog.hold-more-options');
    if (!dialog) return;

    const radio = event.target.closest('.hold-more-options__radio');
    if (radio) advanceFrom(dialog, radio);
  });

  // Consent is asked for only when an address has been typed: a tickbox
  // beside an empty optional field is a question about nothing.
  document.addEventListener('input', (event) => {
    if (!event.target.matches('.hold-more-options input[type="email"]')) return;
    const dialog = event.target.closest('dialog.hold-more-options');
    const consent = dialog?.querySelector('[data-hold-more-options-consent]');
    if (consent) consent.hidden = event.target.value.trim() === '';
  });

  /**
   * A trigger with no dialog behind it must not be offered. That happens when
   * the feature is on but no Google Sheet endpoint is configured yet: the
   * snippet renders the triggers and skips the dialog.
   */
  const hideOrphanTriggers = () => {
    document.querySelectorAll('[data-hold-more-options-open]').forEach((trigger) => {
      if (!document.getElementById(trigger.dataset.holdMoreOptionsOpen)) {
        trigger.hidden = true;
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hideOrphanTriggers, { once: true });
  } else {
    hideOrphanTriggers();
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.hold-more-options__form');
    if (!form) return;
    event.preventDefault();
    const dialog = form.closest('dialog.hold-more-options');
    if (dialog) submit(dialog, true);
  });
})();
